import { useState } from 'react'
import { registro, mensagemDoErro } from '../services/api'
import { Card, Titulo, Botao, Provedor, Etiqueta, Erro, Vazio, dinheiro, numero } from '../components/ui'

/**
 * Comparar candidatos.
 *
 * Nome de versão não diz nada sobre qualidade. O que diz é medição contra os
 * seus dados — foi assim que o gpt-oss-20b ganhou do 120b no estúdio.
 *
 * Duas metades, e a ordem na tela é deliberada. O **observado** vem primeiro
 * porque é de graça e já está lá: sai do tráfego real coletado, não gasta chave
 * e não inventa nada. O **ensaio** vem depois porque gasta dinheiro de verdade
 * — só se usa quando a primeira metade não responde, que é o caso de um modelo
 * que ainda não roda.
 */
export default function Comparar() {
  const [feature, setFeature] = useState('')
  const [observado, setObservado] = useState(null)
  const [erro, setErro] = useState('')

  const [candidatos, setCandidatos] = useState('gpt-4o-mini@openai\ngpt-oss-20b@groq')
  const [casos, setCasos] = useState(
    'Classifique o nicho: "clínica de estética em BH". Responda só o nicho.|||estética\n' +
    'Classifique o nicho: "loja de peças de moto". Responda só o nicho.|||automotivo',
  )
  const [json, setJson] = useState(false)
  const [ensaio, setEnsaio] = useState(null)
  const [rodando, setRodando] = useState(false)

  async function ver(e) {
    e.preventDefault()
    setErro('')
    try {
      setObservado(await registro.comparacao(feature.trim()))
    } catch (err) { setErro(mensagemDoErro(err)) }
  }

  async function rodar() {
    setErro(''); setRodando(true)
    try {
      const lista = candidatos.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
        const [model, provider] = l.split('@')
        return { model: (model || '').trim(), provider: (provider || '').trim() }
      })
      const cs = casos.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
        const [prompt, esperado] = l.split('|||')
        return {
          prompt: prompt.trim(),
          ...(esperado ? { esperado: esperado.trim() } : {}),
          ...(json ? { formato: 'json' } : {}),
        }
      })
      setEnsaio(await registro.ensaiar(lista, cs))
    } catch (err) { setErro(mensagemDoErro(err)) } finally { setRodando(false) }
  }

  const nCandidatos = candidatos.split('\n').filter((l) => l.trim()).length
  const nCasos = casos.split('\n').filter((l) => l.trim()).length

  return (
    <div className="flex flex-col gap-10">
      <Erro>{erro}</Erro>

      <section className="flex flex-col gap-4">
        <Titulo sub="Sai do tráfego já coletado: não gasta chave e não inventa nada. Só fala de modelos que já rodam.">
          O que já rodou
        </Titulo>

        <form onSubmit={ver} className="flex flex-wrap gap-2">
          <input
            value={feature}
            onChange={(e) => setFeature(e.target.value)}
            placeholder="feature — ex: lead_chat, kit_generation"
            className="min-w-[18rem] flex-1 rounded-lg border border-zinc-300 px-3 py-2 font-mono text-sm"
          />
          <Botao type="submit" disabled={!feature.trim()}>Ver</Botao>
        </form>

        {observado && (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-[11px] uppercase tracking-wide text-zinc-500">
                  <th className="p-3 font-semibold">Modelo</th>
                  <th className="p-3 text-right font-semibold">Chamadas</th>
                  <th className="p-3 text-right font-semibold">Mediana</th>
                  <th className="p-3 text-right font-semibold">p95</th>
                  <th className="p-3 text-right font-semibold">US$ / mil</th>
                  <th className="p-3 text-right font-semibold">Falhas</th>
                </tr>
              </thead>
              <tbody>
                {observado.models.map((m) => (
                  <tr key={m.model} className="border-b border-zinc-100 last:border-0">
                    <td className="p-3">
                      <span className="font-mono text-xs">{m.model}</span>{' '}
                      <Provedor id={m.provider} />
                      {m.semPreco > 0 && <span className="ml-2"><Etiqueta tom="aviso">sem preço</Etiqueta></span>}
                    </td>
                    <td className="p-3 text-right font-mono text-xs tabular-nums">{numero(m.chamadas)}</td>
                    <td className="p-3 text-right font-mono text-xs tabular-nums">{numero(m.latenciaMediana)}ms</td>
                    <td className="p-3 text-right font-mono text-xs tabular-nums">{numero(m.latenciaP95)}ms</td>
                    <td className="p-3 text-right font-mono text-xs tabular-nums">{dinheiro(m.custoPorMilChamadas)}</td>
                    <td className="p-3 text-right font-mono text-xs tabular-nums">{numero(m.falhas)}</td>
                  </tr>
                ))}
                {observado.models.length === 0 && (
                  <tr><td colSpan={6}><Vazio>Nenhuma chamada com essa feature no período.</Vazio></td></tr>
                )}
              </tbody>
            </table>
          </Card>
        )}

        {observado?.models?.length > 0 && (
          <p className="max-w-[74ch] text-xs leading-relaxed text-zinc-500">
            Mediana e p95, nunca média: a média de latência esconde o que dói. Um modelo com mediana
            de 300ms e p95 de 12s atende bem quase sempre e trava na frente do cliente de vez em
            quando — na média, ele e um estável de 800ms parecem iguais.
          </p>
        )}
      </section>

      <section className="flex flex-col gap-4">
        <Titulo sub="O único jeito de avaliar um modelo que ainda não roda. São chamadas reais aos provedores — gasta dinheiro.">
          Ensaio contra os seus casos
        </Titulo>

        <Card className="border-amber-200 bg-amber-50/50 p-4 text-sm text-zinc-700">
          O coletor guarda tokens, custo e latência, mas <b>nunca o prompt nem a resposta</b> — é
          privacidade certa e não vai mudar. O preço dessa escolha é este: não há tráfego real para
          reexecutar, então os casos abaixo precisam ser escritos por você.
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-zinc-600">
              Candidatos <span className="font-normal text-zinc-400">um por linha, modelo@provedor · máx. 5</span>
            </label>
            <textarea
              value={candidatos} onChange={(e) => setCandidatos(e.target.value)} rows={5}
              className="rounded-lg border border-zinc-300 p-2.5 font-mono text-xs"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-zinc-600">
              Casos{' '}
              <span className="font-normal text-zinc-400">
                um por linha; <span className="font-mono">prompt|||resposta esperada</span> · máx. 25
              </span>
            </label>
            <textarea
              value={casos} onChange={(e) => setCasos(e.target.value)} rows={5}
              className="rounded-lg border border-zinc-300 p-2.5 font-mono text-xs"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-zinc-600">
            <input type="checkbox" checked={json} onChange={(e) => setJson(e.target.checked)} />
            medir se a saída é JSON válido
          </label>
          <Botao onClick={rodar} disabled={rodando || nCandidatos === 0 || nCasos === 0}>
            {rodando ? 'rodando…' : `Rodar ${nCandidatos * nCasos} chamadas`}
          </Botao>
          <span className="text-xs text-zinc-400">
            {nCandidatos} candidato(s) × {nCasos} caso(s) — chamadas reais, cobradas pelo provedor
          </span>
        </div>

        {ensaio && (
          <div className="flex flex-col gap-3">
            {ensaio.resultados.map((r) => (
              <Card key={`${r.provider}/${r.model}`} className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-sm font-semibold">{r.model}</span>
                  <Provedor id={r.provider} />
                  {r.acuracia !== null && (
                    <Etiqueta tom={r.acuracia === 1 ? 'ok' : r.acuracia >= 0.8 ? 'aviso' : 'perigo'}>
                      {Math.round(r.acuracia * 100)}% de acerto
                    </Etiqueta>
                  )}
                  <span className="font-mono text-xs text-zinc-500">
                    mediana {numero(r.latenciaMediana)}ms · p95 {numero(r.latenciaP95)}ms
                  </span>
                  <span className="font-mono text-xs text-zinc-500">
                    {r.custoTotalUsd === null
                      ? 'custo desconhecido — fora da tabela de preços'
                      : `US$ ${dinheiro(r.custoTotalUsd)}`}
                  </span>
                  {r.falhas > 0 && <Etiqueta tom="perigo">{r.falhas} falha(s)</Etiqueta>}
                </div>

                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-800">
                    ver as {r.casos.length} saídas
                  </summary>
                  <div className="mt-2 flex flex-col gap-2">
                    {r.casos.map((c, i) => (
                      <div key={i} className="rounded-lg border border-zinc-200 p-2.5">
                        <div className="mb-1 font-mono text-[11px] text-zinc-400">{c.prompt}</div>
                        <div className="whitespace-pre-wrap font-mono text-xs text-zinc-800">
                          {c.erro ? <span className="text-red-600">{c.erro}</span> : c.saida}
                        </div>
                        {c.acertou !== null && (
                          <div className="mt-1">
                            <Etiqueta tom={c.acertou ? 'ok' : 'perigo'}>
                              {c.acertou ? 'acertou' : 'errou'}
                            </Etiqueta>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              </Card>
            ))}
          </div>
        )}

        <p className="max-w-[74ch] text-xs leading-relaxed text-zinc-500">
          <b>Custo aparece como desconhecido</b> quando o candidato não está na tabela de preços —
          nunca como zero. Somar zero faria o não precificado parecer o mais barato, que é o
          contrário do que a comparação precisa mostrar.
          <br /><br />
          <b>Os casos não ficam salvos ainda.</b> Comparar de novo daqui a três meses significa
          reescrevê-los, e comparação sem os mesmos casos não é comparação. Guardar conjuntos de
          casos é o passo seguinte.
        </p>
      </section>
    </div>
  )
}
